import React from "react";
import PropTypes from "prop-types";
import { Link as ReactRouterLink } from "react-router-dom";
import { observer } from "mobx-react";

/**
 * Abstraction for react router Link. Some component will exists outside the context of a react router and
 * it's not possible to use Link.
 * If there's a onClick handler, then we'll assume there's user special handling and we won't use a real Link
 * component. E.g:
 * <Link to="/my-url" onClick={myFunction} />
 * In the example above the onClick will take a higher priority and be executed if MyFunction is a function and
 * the "my-url" will be ignored. If only the "to" prop is provided  then we'll return a real react-browser <Link>
 * element.
 */
const Link = observer(({ children, to, as: Element, onClick, ...rest }) => {
  // if a 'to' is given, then we'll render a react router Link, otherwise we'll render a normal button or
  // <a> element and bind leverage the onClick so the upper components know how to navigate in the app.
  // At least one of them must be set. If both are set the onClick will take priority and render a
  // non-Link component.
  if (!to && !onClick) {
    throw new Error("Expected at least one property: to or onClick");
  }

  if (onClick) {
    if (Element) {
      return (
        <Element {...rest} onClick={onClick}>
          {children}
        </Element>
      );
    }

    // default element
    return (
      <a
        href="#"
        {...rest}
        onClick={e => {
          e.preventDefault();
          onClick(e);
        }}
      >
        {children}
      </a>
    );
  }

  // return a react router link

  if (Element) {
    return (
      <Element as={ReactRouterLink} to={to} {...rest}>
        {children}
      </Element>
    );
  }

  return (
    <ReactRouterLink to={to} {...rest}>
      {children}
    </ReactRouterLink>
  );
});

Link.propTypes = {
  children: PropTypes.node.isRequired,
  to: PropTypes.string,
  as: PropTypes.elementType,
  onClick: PropTypes.func,
};

export default Link;
